import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { useNavigate } from 'react-router-dom'

interface User {
  id: string
  name: string | null
  email: string
  image: string | null
  createdAt: Date | null
}

interface AuthContextValue {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (token: string, user: User) => void
  logout: () => void
  checkAuth: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

const TOKEN_KEY = 'session.token'
const USER_KEY = 'session.user'

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token)
}

export function deleteToken(): void {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(USER_KEY)
}

export function getStoredUser(): User | null {
  const stored = localStorage.getItem(USER_KEY)
  if (!stored) return null
  try {
    return JSON.parse(stored)
  } catch {
    return null
  }
}

export function setStoredUser(user: User): void {
  localStorage.setItem(USER_KEY, JSON.stringify(user))
}

interface AuthProviderProps {
  children: React.ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const navigate = useNavigate()

  const checkAuth = useCallback(async () => {
    const token = getToken()
    const storedUser = getStoredUser()

    if (!token) {
      setUser(null)
      setIsLoading(false)
      return
    }

    // If we have a stored user, use it while verifying
    if (storedUser) {
      setUser(storedUser)
    }

    try {
      // Verify token with backend
      const apiUrl = import.meta.env.VITE_API_URL || ''
      const response = await fetch(`${apiUrl}/api/auth/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })

      // Only invalidate the session when the server explicitly rejects the token
      if (response.status === 401 || response.status === 403) {
        deleteToken()
        setUser(null)
        return
      }

      // For any other non-OK response (5xx, network issues surfaced as non-ok),
      // keep the existing session rather than logging the user out
      if (!response.ok) {
        return
      }

      const data = await response.json()
      const userData: User = {
        id: data.user?.id || data.id,
        name: data.user?.name || data.name,
        email: data.user?.email || data.email,
        image: data.user?.image || data.image,
        createdAt: data.user?.createdAt ? new Date(data.user.createdAt) : null,
      }
      setUser(userData)
      setStoredUser(userData)

      // Check for pending invitation after successful authentication
      const pendingInvitation = localStorage.getItem('pending_invitation_token')
      if (pendingInvitation && window.location.pathname !== `/invitations/${pendingInvitation}`) {
        // Redirect to the invitation page
        navigate(`/invitations/${pendingInvitation}`)
      }
    } catch {
      // Network error or request aborted (e.g. rapid page refreshes) —
      // keep the cached session so the user isn't logged out on a transient failure
    } finally {
      setIsLoading(false)
    }
  }, [navigate])

  useEffect(() => {
    checkAuth()
  }, [checkAuth])

  const login = useCallback((token: string, userData: User) => {
    setToken(token)
    setStoredUser(userData)
    setUser(userData)
    // Note: Navigation after login is handled by the login page
  }, [])

  const logout = useCallback(() => {
    deleteToken()
    setUser(null)
    navigate('/login')
  }, [navigate])

  const value: AuthContextValue = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    checkAuth,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
